import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowRight, FaCopy, FaMobileAlt } from 'react-icons/fa';
import api from '../../services/api';
import { AcessoLocatario, mensagemDeErro, onboardingService } from '../../services/onboarding';
import { obterNomeEmpresa } from '../../services/seguranca';
import { rastrearTeste } from '../../services/rastreamentoTeste';
import { useAppSelector } from '../../store/hooks';
import CelularLocatario, { calcularEscalaCelular, DadosApp, PagamentoApp, TelaApp } from './celular-locatario/CelularLocatario';
import QrLojas from './QrLojas';
import { Aviso, Botao, BotaoIcone, Carregando, Credenciais, Rodape } from './styles';
import { Atalhos, ColunaRoteiro, Entrega, GradeAppLocatario, RotuloCelular } from './celular-locatario/estilosEtapa';

const ANDROID = 'https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras';
const IOS = 'https://apps.apple.com/br/app/locasmartpro-app-locat%C3%A1rio/id6757689416';

const PERIODICIDADES: Record<number, string> = { 1: 'SEMANAL', 2: 'QUINZENAL', 3: 'MENSAL', 4: 'DIÁRIO' };

interface Props {
    idLocacao: string;
    /** Acesso já gerado nesta locação (guardado no progresso): reaproveitado sem trocar a senha. */
    acessoSalvo?: AcessoLocatario;
    aoGerarAcesso: (acesso: AcessoLocatario) => void;
    aoConcluir: () => void;
}

interface Passo {
    tela: TelaApp;
    destaque: 'pix' | 'upload' | null;
    titulo: string;
    texto: string;
}

const PASSOS: Passo[] = [
    { tela: 'inicio', destaque: null, titulo: 'Vê o veículo', texto: 'Vê o veículo e quanto paga, sem precisar te perguntar.' },
    { tela: 'pagamentos', destaque: 'pix', titulo: 'Paga pelo PIX', texto: 'Paga as parcelas pelo PIX, direto no app.' },
    { tela: 'vistoria', destaque: 'upload', titulo: 'Envia vistoria', texto: 'Envia vistoria e troca de óleo com fotos, que chegam no seu painel.' },
    { tela: 'documentos', destaque: null, titulo: 'Vê o contrato', texto: 'Tem o contrato que você acabou de gerar sempre à mão.' },
];

const PASSO_DA_TELA: Partial<Record<TelaApp, number>> = { inicio: 0, pagamentos: 1, vistoria: 2, oleo: 2, documentos: 3 };

const calcularEscala = calcularEscalaCelular;

const formatarData = (data?: string) => (data ? new Date(data).toLocaleDateString('pt-BR') : '');

function montarDados(locacao: any, pagamentos: any[], nomeLocadora: string, linkContrato?: string): DadosApp {
    const locatario = locacao?.locatario ?? {};
    const veiculo = locacao?.veiculo ?? {};
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const lista: PagamentoApp[] = [...(pagamentos ?? [])]
        .sort((a, b) => new Date(a.dataPrevista).getTime() - new Date(b.dataPrevista).getTime())
        .slice(0, 12)
        .map(p => {
            const pago = p.status === 1;
            const atrasado = !pago && (p.status === 3 || new Date(p.dataPrevista) < hoje);
            return {
                id: p.id,
                valor: Number(p.valor) || 0,
                descricao: (p.descricao || '').trim() || 'Aluguel',
                data: formatarData(pago ? (p.dataPagamentoRealizado || p.dataPrevista) : p.dataPrevista),
                status: pago ? 'pago' : atrasado ? 'atraso' : 'aberto',
            };
        });

    const nome: string = (locatario.nome || 'Cliente').trim();
    const primeiro = nome.split(/\s+/)[0];
    return {
        primeiroNome: primeiro.charAt(0).toUpperCase() + primeiro.slice(1).toLowerCase(),
        nomeLocatario: nome.toUpperCase(),
        documento: locatario.documento || '',
        nomeLocadora: (nomeLocadora || '').toUpperCase(),
        placa: veiculo.placa || '',
        marca: veiculo.marca || '',
        modelo: veiculo.modelo || '',
        ano: veiculo.ano || '',
        cor: veiculo.cor || '',
        carro: veiculo.tipoVeiculo === 1,
        imagem: veiculo.imagem || undefined,
        valorAluguel: Number(locacao?.valorAluguel) || 0,
        periodicidade: PERIODICIDADES[locacao?.periodicidadePagamento] ?? 'MENSAL',
        pagamentos: lista,
        linkContrato,
    };
}

// Acesso guardado que pode ser mostrado de novo: com senha, ou de um cliente que já usa o app.
const acessoCompleto = (acesso?: AcessoLocatario | null) => Boolean(acesso?.login && (acesso.senha || acesso.jaUsaOApp));

export default function EtapaAppLocatario({ idLocacao, acessoSalvo, aoGerarAcesso, aoConcluir }: Props) {
    const token = useAppSelector(state => state.token);
    const [acesso, setAcesso] = useState<AcessoLocatario | null>(acessoCompleto(acessoSalvo) ? acessoSalvo! : null);
    const [erroAcesso, setErroAcesso] = useState('');
    const [dados, setDados] = useState<DadosApp | null>(null);
    const [erroDados, setErroDados] = useState('');
    const [tela, setTela] = useState<TelaApp>('inicio');
    const [passo, setPasso] = useState(0);
    const [escala, setEscala] = useState(calcularEscala);
    const pedido = useRef(false);

    useEffect(() => {
        if (pedido.current) return;
        pedido.current = true;
        // Cada geração troca a senha do cliente: só gera quando ainda não há acesso guardado.
        if (!acessoCompleto(acessoSalvo)) {
            onboardingService.gerarAcessoLocatario(idLocacao)
                .then(novo => { setAcesso(novo); aoGerarAcesso(novo); })
                .catch(e => setErroAcesso(mensagemDeErro(e, 'Não foi possível gerar o acesso do seu cliente.')));
        }

        Promise.all([
            api.get(`/Locacao/${idLocacao}`),
            api.get(`/Pagamento/PorLocacao/${idLocacao}`).catch(() => ({ data: [] })),
            api.get(`/Locacao/ComDocumentoContrato/${idLocacao}`).catch(() => ({ data: null })),
        ])
            .then(([locacao, pagamentos, contrato]) =>
                setDados(montarDados(locacao.data, pagamentos.data as any[], obterNomeEmpresa(token), (contrato.data as any)?.documentoContrato)))
            .catch(e => setErroDados(mensagemDeErro(e, 'Não foi possível carregar a locação para a demonstração.')));
    }, [idLocacao, token]);

    // Cada tela do app que a pessoa abre (pelos atalhos ou tocando no celular), sem a inicial.
    const primeiraTela = useRef(true);
    useEffect(() => {
        if (primeiraTela.current) { primeiraTela.current = false; return; }
        rastrearTeste('onboarding_app_locatario_aba', { aba: tela });
    }, [tela]);

    useEffect(() => {
        const aoRedimensionar = () => setEscala(calcularEscala());
        window.addEventListener('resize', aoRedimensionar);
        return () => window.removeEventListener('resize', aoRedimensionar);
    }, []);

    const irParaPasso = (indice: number) => {
        setPasso(indice);
        setTela(PASSOS[indice].tela);
    };

    const mudarTela = (nova: TelaApp) => {
        setTela(nova);
        const indice = PASSO_DA_TELA[nova];
        if (indice !== undefined) setPasso(indice);
    };

    const copiar = (texto: string, qual: 'login' | 'senha') => {
        rastrearTeste('onboarding_credencial_copiada', { qual });
        navigator.clipboard?.writeText(texto).then(() => toast.success('Copiado.')).catch(() => undefined);
    };

    const nome = dados?.primeiroNome ?? 'seu cliente';
    const destaque = PASSOS[passo].tela === tela ? PASSOS[passo].destaque : null;

    return (
        <>
            <GradeAppLocatario>
                <div className="celular">
                    <RotuloCelular><FaMobileAlt /> App do Locatário (seu cliente)</RotuloCelular>
                    {dados
                        ? <CelularLocatario dados={dados} tela={tela} escala={escala} destaque={destaque} aoMudarTela={mudarTela} />
                        : erroDados ? <Aviso $tipo="erro">{erroDados}</Aviso> : <Carregando />}
                </div>

                <ColunaRoteiro>
                    <h1>Seu cliente ganha um app com a locação dele</h1>
                    <p className="sub">Toque no celular e veja o que {nome} vai ver.</p>

                    <Atalhos>
                        {PASSOS.map((p, i) => (
                            <button type="button" key={p.tela} className={passo === i ? 'ativo' : ''} onClick={() => irParaPasso(i)}>
                                <span>{i + 1}</span>{p.titulo}
                            </button>
                        ))}
                    </Atalhos>
                    <p className="legenda">{PASSOS[passo].texto}</p>

                    <Entrega>
                        <h3>Para {nome} começar a usar</h3>
                        {erroAcesso && <Aviso $tipo="erro">{erroAcesso}</Aviso>}
                        <div className="linha">
                            <QrLojas android={ANDROID} ios={IOS} tamanho={104} />
                            {acesso ? (
                                <Credenciais style={{ marginTop: 0, maxWidth: 420 }}>
                                    <div className="item">
                                        <div><small>Login</small><strong>{acesso.login}</strong></div>
                                        <BotaoIcone type="button" aria-label="Copiar login" onClick={() => copiar(acesso.login, 'login')}><FaCopy /></BotaoIcone>
                                    </div>
                                    {acesso.senha ? (
                                        <div className="item">
                                            <div><small>Senha</small><strong>{acesso.senha}</strong></div>
                                            <BotaoIcone type="button" aria-label="Copiar senha" onClick={() => copiar(acesso.senha!, 'senha')}><FaCopy /></BotaoIcone>
                                        </div>
                                    ) : (
                                        <div className="item">
                                            <div><small>Senha</small><span>{nome} já usa o app e entra com a senha que já tem.</span></div>
                                        </div>
                                    )}
                                </Credenciais>
                            ) : !erroAcesso && <small>Gerando o acesso...</small>}
                        </div>
                    </Entrega>
                </ColunaRoteiro>
            </GradeAppLocatario>

            <Rodape style={{ marginTop: 14, paddingTop: 14 }}>
                <span />
                <Botao type="button" onClick={aoConcluir}>Continuar <FaArrowRight /></Botao>
            </Rodape>
        </>
    );
}
